import type { Store, ReadableAtom, WritableAtom, Task, AnyStore, StoreValue } from 'nanostores';
import { computed } from 'nanostores';
import type { StoreValues } from 'node_modules/nanostores/computed';
import { NOOP } from './noops';
import { withResolvers } from './promises';
import { invariant } from '~/utils/invariant';

const SENTINEL = {};

export interface WritableAtomHalf<T> {
  set(value: T): void;
}

export function isAtom(object: any): object is WritableAtom {
  return object != null && typeof object.set === 'function' && typeof object.get === 'function' && 'value' in object;
}

type Predicate<T> = (value: T) => boolean;

export function waitUntil<T>(
  atom: ReadableAtom<T>,
  guardValue: T | Predicate<T>,
  { signal }: { signal?: AbortSignal } = {},
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);

      return;
    }

    function onAbort() {
      unsubscribe();
      reject(signal?.reason);
    }

    signal?.addEventListener('abort', onAbort);

    const unsubscribe = waitOnce(atom, guardValue, (value) => {
      resolve(value);
      signal?.removeEventListener('abort', onAbort);
    });
  });
}

function waitOnce<T>(atom: ReadableAtom<T>, guardValue: T | Predicate<T>, callback: (value: T) => void) {
  /**
   * The cast is only valid if T is not a function. While atom can hold anything there
   * aren't much use cases for having them hold a function right now.
   */
  const checkValue: Predicate<T> =
    typeof guardValue === 'function' ? (guardValue as Predicate<T>) : (value: T) => value === guardValue;

  let stopped = false;

  let unsubscribe = NOOP;

  unsubscribe = atom.subscribe((value) => {
    if (checkValue(value)) {
      unsubscribe();
      stopped = true;

      callback(value);
    }
  });

  if (stopped) {
    unsubscribe();
  }

  return unsubscribe;
}

export function watchAll<OriginStores extends Store[]>(...stores: OriginStores) {
  /**
   * This implementation really should have been just this:
   *
   * ```
   * computed<StoreValues<OriginStores>, OriginStores>(stores, (...values) => values);
   * ```
   *
   * However, this does not work if one of the stores is a computed value.
   */

  const unsubscribeCallbacks: VoidFunction[] = [];
  const values = stores.map(() => undefined) as StoreValues<OriginStores>;

  let subscribed = false;
  let initialzed = false;

  return {
    subscribe(listener: (value: StoreValues<OriginStores>, oldValue?: StoreValues<OriginStores>) => void): () => void {
      invariant(!subscribed);

      subscribed = true;

      for (let index = 0; index < stores.length; ++index) {
        const store = stores[index];

        unsubscribeCallbacks.push(
          store.subscribe((value) => {
            values[index] = value;

            if (initialzed) {
              listener(values);
            }
          }),
        );
      }

      listener(values);

      initialzed = true;

      return () => {
        subscribed = false;

        unsubscribeCallbacks.forEach((callback) => callback());
        unsubscribeCallbacks.splice(0, unsubscribeCallbacks.length);
      };
    },
  };
}

export function waitForFirstValue<T>(atom: ReadableAtom<T>) {
  return new Promise<NonNullable<T>>((resolve) => {
    const currentValue = atom.get();

    if (currentValue != null) {
      resolve(currentValue);

      return;
    }

    const unsubscribe = atom.subscribe((value) => {
      if (value != null) {
        unsubscribe();
        resolve(value);
      }
    });
  });
}

/**
 * Derived works like nanostores computed but has an extra
 * context in addition which makes it for the callback to
 * cache the derived data if it think it hasn't changed.
 */
export function derived<V, C, S extends Store>(
  stores: S,
  context: C,
  cb: (context: C, value: StoreValue<S>) => Task<V> | V,
): ReadableAtom<V>;
export function derived<V, C, S extends AnyStore[]>(
  stores: [...S],
  context: C,
  cb: (context: C, ...values: StoreValues<S>) => Task<V> | V,
): ReadableAtom<V> {
  return computed(stores, (...values) => cb(context, ...values));
}

export function asyncValues<T>(atom: ReadableAtom<T>) {
  let deferred = withResolvers<void>();
  let last: T | {} = SENTINEL;

  atom.subscribe((value) => {
    last = value;
    deferred.resolve();
  });

  async function* iter() {
    while (true) {
      while (last === SENTINEL) {
        await deferred.promise;
      }

      const value = last as T;
      last = SENTINEL;
      deferred = withResolvers();

      yield value;
    }
  }

  return iter();
}
